import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import { SelectButton } from "primereact/selectbutton";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useGetRemitAccuracySummaryQuery } from "store/queries/remitAccuracy";
import {
  IRemitAccuracyAccount,
  IRemitAccuracyFilters,
  IRemitClusterRequest,
  IRemitHeadlineCell,
  IRemitMissCluster,
  IRemitNotConfiguredCallout,
  RemitPickKind,
  RemitScoredBenefit,
} from "types/RemitAccuracy/RemitAccuracy";

import { RemitAccuracyCluster } from "./RemitAccuracyCluster";
import { RemitAccuracyExcludedDialog } from "./RemitAccuracyExcludedDialog";
import {
  accuracyText,
  DATE_OF_SERVICE_OPTIONS,
  dateOfServiceRange,
  DateOfServicePreset,
  emptyKindText,
  exclusionLabel,
  formatRange,
  formatRemitValue,
  percentOf,
  READ_FROM_LABELS,
  REMIT_BENEFIT_LABELS,
  REMIT_KIND_LABELS,
  remitShareWording,
} from "./remitAccuracyWording";

const BENEFITS: RemitScoredBenefit[] = [
  "COINSURANCE_PERCENT",
  "FLAT_COPAY",
  "PROFESSIONAL_COPAY",
];
const OTHER_KIND: Record<RemitPickKind, RemitPickKind> = {
  REPLAY: "QUOTED",
  QUOTED: "REPLAY",
};

const cellFor = (
  cells: IRemitHeadlineCell[],
  kind: RemitPickKind,
  benefit: RemitScoredBenefit
) => cells.find((cell) => cell.kind === kind && cell.benefit === benefit);

/** Nothing at all has been recorded for a kind yet — QUOTED rows only exist from the deploy on. */
const kindIsEmpty = (cells: IRemitHeadlineCell[], kind: RemitPickKind) =>
  cells
    .filter((cell) => cell.kind === kind)
    .every(
      (cell) =>
        cell.scored === 0 &&
        cell.notConfigured === 0 &&
        cell.remitsDisagree === 0
    );

const dateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : "—";

function AccuracyBar({ cell }: { cell: IRemitHeadlineCell }) {
  return (
    <div className="flex align-items-center gap-2">
      <ProgressBar
        value={percentOf(cell) ?? 0}
        showValue={false}
        style={{ width: "7rem", height: "0.4rem" }}
      />
      <span>{accuracyText(cell)}</span>
    </div>
  );
}

function TileBody({
  benefit,
  kind,
  cells,
}: {
  benefit: RemitScoredBenefit;
  kind: RemitPickKind;
  cells: IRemitHeadlineCell[];
}) {
  const cell = cellFor(cells, kind, benefit);
  if (kindIsEmpty(cells, kind)) {
    return (
      <span className="text-2xl font-semibold text-color-secondary">
        {emptyKindText(kind)}
      </span>
    );
  }
  if (!cell || cell.scored === 0) {
    return (
      <span className="text-2xl font-semibold text-color-secondary">
        Not measured · 0 checks
      </span>
    );
  }
  return (
    <>
      <div className="flex align-items-baseline gap-2">
        <span className="text-4xl font-semibold">
          {(percentOf(cell) as number).toFixed(1)}%
        </span>
        <span className="text-color-secondary">
          {cell.match.toLocaleString()} of {cell.scored.toLocaleString()} checks
        </span>
      </div>
      <ProgressBar
        value={percentOf(cell) ?? 0}
        showValue={false}
        style={{ height: "0.5rem" }}
      />
      {benefit !== "COINSURANCE_PERCENT" && (
        <span className="text-color-secondary">
          {cell.scoredZeroTruth.toLocaleString()} confirmed $0 ·{" "}
          {cell.scoredPositiveTruth.toLocaleString()} confirmed above $0
        </span>
      )}
    </>
  );
}

function AccountCell({
  account,
  kind,
  benefit,
}: {
  account: IRemitAccuracyAccount;
  kind: RemitPickKind;
  benefit: RemitScoredBenefit;
}) {
  const cell = cellFor(account.cells, kind, benefit);
  // What was scored comes first: an account whose map rows were all retired still has its counts.
  if (cell && cell.scored > 0) return <AccuracyBar cell={cell} />;
  if (cell && cell.notConfiguredWithCopay > 0)
    return (
      <span className="text-orange-700">
        Not configured · {cell.notConfiguredWithCopay} checks{" "}
        {remitShareWording(benefit)}
      </span>
    );
  if (cell && cell.notConfigured > 0)
    return <span className="text-orange-700">Not configured</span>;
  if (account.linkedClaims === 0)
    return (
      <span className="text-color-secondary">
        Not measured · no linked remits
      </span>
    );
  if (!account.configured)
    return (
      <span className="text-color-secondary">
        Not measured · no benefit configuration
      </span>
    );
  return <span className="text-color-secondary">Not measured</span>;
}

function HeadlineTile({
  benefit,
  kind,
  cells,
}: {
  benefit: RemitScoredBenefit;
  kind: RemitPickKind;
  cells: IRemitHeadlineCell[];
}) {
  const other = cellFor(cells, OTHER_KIND[kind], benefit);
  const otherText = kindIsEmpty(cells, OTHER_KIND[kind])
    ? `${emptyKindText(OTHER_KIND[kind]).toLowerCase()} · 0 scored`
    : accuracyText(other);

  return (
    <div
      className="border-1 surface-border border-round p-3 h-full flex flex-column gap-2"
      data-testid={`remitAccuracy.tile.${benefit}`}
    >
      <span className="font-semibold text-color-secondary">
        {REMIT_BENEFIT_LABELS[benefit]}
      </span>
      <TileBody benefit={benefit} kind={kind} cells={cells} />
      <span className="text-color-secondary">
        {REMIT_KIND_LABELS[OTHER_KIND[kind]]}: {otherText}
      </span>
    </div>
  );
}

/**
 * CLE-2818 — REMIT ACCURACY: how often the benefit engine's pick agrees with what the payer applied on the
 * 835 remit. Internal, SuperAdmin only; rebuilt nightly. A percentage is never shown without its sample
 * size, and an account with nothing to measure reads "not measured", never a low score.
 */
export function RemitAccuracyTab() {
  const [kind, setKind] = useState<RemitPickKind>("REPLAY");
  const [accountId, setAccountId] = useState<string | undefined>();
  const [payerId, setPayerId] = useState<string | undefined>();
  const [benefit, setBenefit] = useState<RemitScoredBenefit | undefined>();
  const [dateOfService, setDateOfService] =
    useState<DateOfServicePreset>("ALL");
  const [cluster, setCluster] = useState<IRemitClusterRequest | null>(null);
  const [excludedReason, setExcludedReason] = useState<string | null>(null);

  const filters: IRemitAccuracyFilters = useMemo(
    () => ({
      accountId,
      payerId,
      benefit,
      ...dateOfServiceRange(dateOfService),
    }),
    [accountId, payerId, benefit, dateOfService]
  );
  const { data, isLoading, isError } = useGetRemitAccuracySummaryQuery(filters);
  const summary = data?.data;
  const cells = summary?.headline ?? [];
  const benefits = benefit ? [benefit] : BENEFITS;

  if (cluster) {
    return (
      <Surface data-testid="remitAccuracyTab">
        <RemitAccuracyCluster
          request={cluster}
          onBack={() => setCluster(null)}
        />
      </Surface>
    );
  }

  const callouts = (summary?.notConfiguredWithCopays ?? []).filter(
    (callout) => callout.kind === kind
  );
  const clusters = (summary?.missClusters ?? []).filter(
    (each) => each.kind === kind
  );
  const totalMisses = cells
    .filter((cell) => cell.kind === kind)
    .reduce((sum, cell) => sum + cell.scored - cell.match, 0);

  const configureBody = (callout: IRemitNotConfiguredCallout) => (
    <Link to={`/hospitals/edit/${callout.accountId}`}>Configure ›</Link>
  );

  const clusterNameBody = (row: IRemitMissCluster) => (
    <div className="flex flex-column gap-1">
      <span className="font-semibold">
        {row.accountName} · {row.payerName}
      </span>
      <span className="text-color-secondary">
        {row.planName ?? "No plan name"}
      </span>
    </div>
  );

  const openClusterBody = (row: IRemitMissCluster) => (
    <Button
      type="button"
      label="Open ›"
      className="p-button-text p-button-sm"
      data-testid="remitAccuracy.cluster.open"
      onClick={() =>
        setCluster({
          ...filters,
          kind: row.kind,
          accountId: row.accountId,
          payerId: row.payerId === null ? "none" : String(row.payerId),
          ...(row.planName === null
            ? { planNameMissing: true }
            : { planName: row.planName }),
          benefit: row.benefit,
          readFrom: row.readFrom,
        })
      }
    />
  );

  const coinsuranceAccountBody = (account: IRemitAccuracyAccount) => (
    <AccountCell account={account} kind={kind} benefit="COINSURANCE_PERCENT" />
  );
  const facilityAccountBody = (account: IRemitAccuracyAccount) => (
    <AccountCell account={account} kind={kind} benefit="FLAT_COPAY" />
  );
  const physicianAccountBody = (account: IRemitAccuracyAccount) => (
    <AccountCell account={account} kind={kind} benefit="PROFESSIONAL_COPAY" />
  );

  return (
    <Surface data-testid="remitAccuracyTab">
      <div className="flex flex-wrap justify-content-between gap-2 mb-3">
        <span className="text-color-secondary">
          How often the benefit engine&apos;s pick agrees with what the payer
          actually applied on the 835 remit.
        </span>
        <span
          className="text-color-secondary"
          data-testid="remitAccuracy.lastRun"
        >
          Last nightly run: {dateTime(summary?.computedAt ?? null)} ·{" "}
          {(summary?.remits ?? 0).toLocaleString()} remits ·{" "}
          {(summary?.checks ?? 0).toLocaleString()} checks · engine config as of{" "}
          {dateTime(summary?.configAsOf ?? null)}
        </span>
      </div>

      <div className="flex flex-wrap align-items-end gap-3 mb-3">
        <div className="flex flex-column gap-1">
          <label htmlFor="remitAccuracy.account">Account</label>
          <Dropdown
            inputId="remitAccuracy.account"
            value={accountId ?? null}
            options={summary?.options.accounts ?? []}
            optionLabel="accountName"
            optionValue="accountId"
            placeholder="All accounts"
            showClear
            filter
            className="w-16rem"
            onChange={(e) => setAccountId(e.value ?? undefined)}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="remitAccuracy.payer">Payer</label>
          <Dropdown
            inputId="remitAccuracy.payer"
            value={payerId ?? null}
            options={(summary?.options.payers ?? []).map((payer) => ({
              label: payer.payerName,
              value: String(payer.payerId),
            }))}
            placeholder="All payers"
            showClear
            filter
            className="w-14rem"
            onChange={(e) => setPayerId(e.value ?? undefined)}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="remitAccuracy.benefit">Benefit</label>
          <Dropdown
            inputId="remitAccuracy.benefit"
            value={benefit ?? null}
            options={BENEFITS.map((each) => ({
              label: REMIT_BENEFIT_LABELS[each],
              value: each,
            }))}
            placeholder="All benefits"
            showClear
            className="w-12rem"
            onChange={(e) => setBenefit(e.value ?? undefined)}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="remitAccuracy.dateOfService">Date of service</label>
          <Dropdown
            inputId="remitAccuracy.dateOfService"
            value={dateOfService}
            options={DATE_OF_SERVICE_OPTIONS}
            className="w-12rem"
            onChange={(e) => setDateOfService(e.value)}
          />
        </div>
        <div className="flex flex-column gap-1 ml-auto">
          <span>Engine pick</span>
          <SelectButton
            value={kind}
            options={(["REPLAY", "QUOTED"] as RemitPickKind[]).map((value) => ({
              value,
              label: REMIT_KIND_LABELS[value],
            }))}
            onChange={(e) => e.value && setKind(e.value)}
            data-testid="remitAccuracy.kind"
          />
        </div>
      </div>

      {isError && (
        <Message severity="error" text="Remit accuracy could not be loaded." />
      )}

      <div className="grid mb-3">
        {benefits.map((each) => (
          <div key={each} className="col-12 md:col-4">
            <HeadlineTile benefit={each} kind={kind} cells={cells} />
          </div>
        ))}
      </div>

      {callouts.length > 0 && (
        <div
          id="remit-accuracy-not-configured"
          className="border-1 border-yellow-300 bg-yellow-50 border-round p-3 mb-3"
          data-testid="remitAccuracy.notConfigured"
        >
          <div className="flex flex-wrap align-items-center gap-2 mb-2">
            <i className="pi pi-exclamation-triangle text-yellow-700" />
            <span className="font-semibold">
              Not configured, but remits show copays
            </span>
            <span className="text-color-secondary">
              The engine declines these today, so at cutover they fall back to
              legacy.
            </span>
          </div>
          <DataTable value={callouts} dataKey="accountId">
            <Column field="accountName" header="Account" />
            <Column
              header="Benefit"
              body={(row: IRemitNotConfiguredCallout) =>
                REMIT_BENEFIT_LABELS[row.benefit]
              }
            />
            <Column field="checksWithCopay" header="Checks with a copay" />
            <Column field="checksWithoutCopay" header="Checks without" />
            <Column
              header="Most common remit copay"
              body={(row: IRemitNotConfiguredCallout) =>
                formatRemitValue(row.mostCommonCopay, row.benefit)
              }
            />
            <Column header="" body={configureBody} />
          </DataTable>
        </div>
      )}

      <div className="flex flex-wrap justify-content-between align-items-baseline mb-2">
        <div>
          <h3 className="m-0">Biggest miss clusters</h3>
          <span className="text-color-secondary">
            Checks where the engine disagrees with the remit, grouped by
            account, payer, plan, benefit and where the engine read from. Ranked
            by misses.
          </span>
        </div>
        <span className="text-color-secondary">
          {totalMisses.toLocaleString()} misses in total
        </span>
      </div>
      <DataTable
        value={clusters}
        loading={isLoading}
        emptyMessage={
          kindIsEmpty(cells, kind) ? `${emptyKindText(kind)}.` : "No misses."
        }
        className="mb-4"
        data-testid="remitAccuracy.missClusters"
      >
        <Column
          header="#"
          body={(_row, options) => options.rowIndex + 1}
          style={{ width: "3rem" }}
        />
        <Column header="Account · Payer · Plan" body={clusterNameBody} />
        <Column
          header="Benefit"
          body={(row: IRemitMissCluster) => REMIT_BENEFIT_LABELS[row.benefit]}
        />
        <Column
          header="Misses"
          body={(row: IRemitMissCluster) => `${row.misses} of ${row.checks}`}
        />
        <Column
          header="Engine"
          body={(row: IRemitMissCluster) =>
            formatRange(
              row.engineMin,
              row.engineMax,
              row.benefit,
              row.engineHadNoValue
            )
          }
        />
        <Column
          header="Remit"
          body={(row: IRemitMissCluster) =>
            formatRange(row.remitMin, row.remitMax, row.benefit)
          }
        />
        <Column
          header="Engine read from"
          body={(row: IRemitMissCluster) =>
            READ_FROM_LABELS[row.readFrom] ?? row.readFrom
          }
        />
        <Column header="" body={openClusterBody} />
      </DataTable>

      <h3 className="mb-1">By account</h3>
      <span className="text-color-secondary">
        An account with no remits linked to an estimate is not measured. That
        says nothing about how accurate it is.
      </span>
      <DataTable
        value={summary?.accounts ?? []}
        dataKey="accountId"
        loading={isLoading}
        paginator
        rows={20}
        className="mt-2 mb-4"
        data-testid="remitAccuracy.accounts"
      >
        <Column field="accountName" header="Account" />
        <Column header="Co-insurance" body={coinsuranceAccountBody} />
        <Column header="Facility copay" body={facilityAccountBody} />
        <Column header="Physician copay" body={physicianAccountBody} />
      </DataTable>

      <div
        className="border-1 surface-border border-round p-3"
        data-testid="remitAccuracy.excluded"
      >
        <div className="flex flex-wrap align-items-baseline gap-2 mb-2">
          <h3 className="m-0">Remits not scored</h3>
          <span className="text-color-secondary">
            {(summary?.exclusions.notScored ?? 0).toLocaleString()} of{" "}
            {(summary?.exclusions.remits ?? 0).toLocaleString()}. Each one is
            excluded because its remit can&apos;t be trusted as the answer for
            that check.
          </span>
        </div>
        <div className="grid">
          {(summary?.exclusions.reasons ?? []).map((each) => (
            <div key={each.reason} className="col-12 md:col-3">
              <div className="border-1 surface-border border-round p-2">
                <Button
                  type="button"
                  className="p-button-text p-0 text-xl font-semibold"
                  label={each.count.toLocaleString()}
                  onClick={() => setExcludedReason(each.reason)}
                />
                <div className="text-color-secondary">
                  {exclusionLabel(each.reason)}
                </div>
              </div>
            </div>
          ))}
        </div>
        <span className="text-xs text-color-secondary">
          Remits with no patient share can&apos;t confirm a copay or a
          co-insurance rate, and aren&apos;t counted as misses.
        </span>
      </div>

      <RemitAccuracyExcludedDialog
        reason={excludedReason}
        filters={filters}
        onHide={() => setExcludedReason(null)}
      />
    </Surface>
  );
}
